import { useCallback, useEffect, useRef } from 'react';
import { useGameInstance } from '../contexts/GameInstanceContext';
import { suspendPhaserKeyboard } from '../utils/phaser-keyboard.js';

/**
 * Suspends Phaser keyboard capture while a text field is focused, so typing
 * does not move the player, and gives it back on blur.
 *
 * Focus takes a `text-field` hold on the keyboard and blur releases it
 * (`utils/phaser-keyboard.ts`). The keyboard only comes back when nothing
 * else holds it either, so a field inside an open Modal, or the chat composer
 * during full-screen video, cannot re-enable player movement when it blurs.
 * Taking the hold also resets held keys, so no key stays "down" while the
 * player types.
 *
 * Includes a failsafe: if the field unmounts while still focused (modal or
 * drawer closing), the hold is released anyway. Without it, Phaser keyboard
 * input stays dead until the page is reloaded.
 *
 * No-op until the game instance exists (pre-game screens), so it is safe to
 * use in any input component.
 */
export function usePhaserKeyboardSuspend() {
  const { gameInstance } = useGameInstance();
  const gameRef = useRef(gameInstance);
  const releaseRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    gameRef.current = gameInstance;
  }, [gameInstance]);

  const onFocus = useCallback(() => {
    // A second focus without a blur in between must not leak the first hold.
    releaseRef.current?.();
    releaseRef.current = suspendPhaserKeyboard(gameRef.current, 'text-field');
  }, []);

  const onBlur = useCallback(() => {
    releaseRef.current?.();
    releaseRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      releaseRef.current?.();
      releaseRef.current = null;
    };
  }, []);

  return { onFocus, onBlur };
}
